import { FlexColumn } from "@/components/Flex";
import { DEFAULT_SIDEBAR_WIDTH, useSidebarContextSelector } from "@/components/NotePage/hooks/SidebarContext";
import { useBreakPoints } from "@/hooks/styles/useBreakpoints";
import { useMounted } from "@/hooks/useMounted";
import { themeColors } from "@/utils/themeColors";
import Box from "@mui/material/Box";
import { memo, useEffect } from "react";
import SidebarBody from "../styled/SidebarBody";
import {
    ChatButton,
    ClassPerformanceButton,
    FlashcardsButton,
    MediaButton,
    PracticeTestButton,
    UnitButton,
} from "../SidebarButtons";
import Explorer from "./Explorer";
import FiltersBar from "../FiltersBar";
import FullScreenButton from "../../FullScreenButton";
import br from "@/styles/breakpoints.module.css";
import SearchBar from "../SearchBar";
import { spacing } from "@/utils/spacing";

const HoverableChevronToggler = ({ onClick }: { onClick: () => void }) => {
    return (
        <Box
            component="button"
            className={"strippedBtn"}
            onClick={onClick}
            sx={{
                position: "fixed",
                top: "50%",
                left: "0rem",
                opacity: 0,
                transform: "translateY(-50%)",
                height: "100%",
                zIndex: 2,
                width: "6rem",
                transition: "width 0.2s ease-out, opacity 0.3s ease",
                "&&:hover": { width: "7rem", opacity: 1 },
            }}>
            <span
                style={{
                    marginLeft: "auto",
                    display: "block",
                    borderColor: themeColors.neutral2,
                    borderStyle: "solid",
                    borderWidth: "0.4rem 0.4rem 0 0",
                    height: "2.4rem",
                    width: "2.4rem",
                    transform: "rotate(45deg)",
                    borderRadius: 2,
                }}
            />
        </Box>
    );
};

const NavigationPanel = ({ style }: { style: React.CSSProperties }) => {
    const { mdDown, smDown } = useBreakPoints();

    const isNoteSidebarCollapse = useSidebarContextSelector(s => s.isNoteSidebarCollapse);
    const toggleNoteSidebar = useSidebarContextSelector(s => s.toggleNoteSidebar);
    const collapseNoteSidebar = useSidebarContextSelector(s => s.collapseNoteSidebar);

    const mounted = useMounted();
    // on mobile screen, close NavigationPanel by default
    // biome-ignore lint: useExhaustiveDependencies
    useEffect(() => {
        if (!mounted) return;

        if (!isNoteSidebarCollapse && smDown) {
            collapseNoteSidebar();
        }
    }, [mounted]);

    useEffect(() => {
        if (mdDown) {
            collapseNoteSidebar();
        }
    }, [mdDown, collapseNoteSidebar]);

    return (
        <>
            <SidebarBody
                className="scrollbar"
                style={{
                    height: "100vh",
                    overflow: !isNoteSidebarCollapse ? "auto" : "hidden",
                    width: !isNoteSidebarCollapse ? (smDown ? "100%" : DEFAULT_SIDEBAR_WIDTH) : 0,
                    padding: !isNoteSidebarCollapse ? "1.5rem 1.75rem 0rem 1.75rem" : "1.5rem 0 0rem 0",
                    backgroundColor: themeColors.neutralWhite,
                    ...style,
                }}>
                <FullScreenButton
                    className={br.smUpDisplayNone}
                    onClick={toggleNoteSidebar}
                    sx={{ position: "static", marginBottom: "2rem" }}
                />

                <FlexColumn style={{ gap: "1.2rem", marginBottom: "2rem" }}>
                    <ClassPerformanceButton />
                    <PracticeTestButton />
                    <ChatButton />
                    <FlashcardsButton />
                    <MediaButton />
                    <UnitButton />
                </FlexColumn>

                <span className="bodyBold2" style={{ marginBottom: "2rem" }}>
                    All Notes
                </span>
                <SearchBar sx={{ marginBottom: spacing.XS_2 }} />
                <FiltersBar style={{ marginBottom: "1.5rem" }} />
                <Explorer />
            </SidebarBody>
            {isNoteSidebarCollapse && !smDown && <HoverableChevronToggler onClick={toggleNoteSidebar} />}
        </>
    );
};

export default memo(NavigationPanel);
